import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, onSnapshot, orderBy, query, runTransaction, setDoc } from "firebase/firestore";
import { database } from "../../database/firebase";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { initQuestions } from "../../functions/setUserQuestions";
import "./waiting.css";

const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";
const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

const CHARACTER_SLOTS = ["c1", "c2", "c3", "c4", "c5"];

// Palette of distinct, game-friendly colors. shuffleArrayDeterministic with a
// stable per-game seed picks each player's color, so every client computes the
// same color for a given player even though the assignment looks random.
const PLAYER_COLOR_PALETTE = [
  "#e74c3c",
  "#3498db",
  "#2ecc71",
  "#f1c40f",
  "#9b59b6",
  "#e67e22",
  "#1abc9c",
  "#ec407a",
];

function sortLiveUsersForCharacterAssign(users) {
  return [...users].sort((a, b) => {
    const ta = Number(a?.timestamp ?? 0);
    const tb = Number(b?.timestamp ?? 0);
    if (ta !== tb) return ta - tb;
    return String(a?.userId ?? a?.id ?? "").localeCompare(
      String(b?.userId ?? b?.id ?? "")
    );
  });
}

function characterForLobbySlotIndex(slotIndex) {
  return CHARACTER_SLOTS[
    Math.min(Math.max(Number(slotIndex) || 0, 0), CHARACTER_SLOTS.length - 1)
  ];
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizeTotalUserCount(value) {
  const n = Number(value);
  if (n === 3 || n === 4) return n;
  return 4;
}

function parseMmSsToSeconds(value, fallbackSeconds) {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return fallbackSeconds;
  const mm = Math.max(0, Math.min(99, Number(match[1]) || 0));
  const ss = Math.max(0, Math.min(59, Number(match[2]) || 0));
  return mm * 60 + ss;
}

function parseGridSize(value, fallback = 6) {
  const m = String(value || "").trim().match(/^(\d)x\1$/);
  if (!m) return fallback;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 3 && n <= 8 ? n : fallback;
}

function shuffleArrayDeterministic(arr, seedStr) {
  const a = [...arr];
  let seed = 0;
  const s = String(seedStr || "");
  for (let i = 0; i < s.length; i++) seed = (seed * 31 + s.charCodeAt(i)) >>> 0;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x100000000;
  };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function resolveLobbyName(locationState, userData) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  try {
    const fromSession = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (fromSession && fromSession.trim()) return fromSession.trim();
  } catch {
    // ignore storage failures
  }
  return String(userData?.lobby ?? "").trim();
}

function stripNullWords(value) {
  return String(value ?? "")
    .replace(/\bnull\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getDisplayName(userData) {
  const name = stripNullWords(userData?.name);
  if (name) return name;
  const first = stripNullWords(userData?.firstName);
  if (first) return first;
  const last = stripNullWords(userData?.lastName);
  if (last) return last;
  return `${first} ${last}`.trim();
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-6]$/.test(value) ? value : "";
}

function isPresenterValue(value) {
  if (value === true) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

function getPlayerLabel(player, fallbackUserData, order) {
  const fromName = stripNullWords(player?.name);
  if (fromName) return fromName;

  const first = stripNullWords(player?.firstName ?? player?.first_name);
  const last = stripNullWords(player?.lastName ?? player?.last_name);
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;

  const fromEmail = String(player?.email ?? "").trim();
  if (fromEmail) return fromEmail;

  const fromEmpId = String(player?.emp_id ?? player?.employeeId ?? "").trim();
  if (fromEmpId) return fromEmpId;

  const fallbackName = stripNullWords(fallbackUserData?.name);
  if (fallbackName) return fallbackName;

  const fallbackEmail = String(fallbackUserData?.email ?? "").trim();
  if (fallbackEmail) return fallbackEmail;

  const fallbackEmpId = String(
    fallbackUserData?.emp_id ?? fallbackUserData?.employeeId ?? ""
  ).trim();
  if (fallbackEmpId) return fallbackEmpId;

  return `Player ${order}`;
}

export default function Waiting() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const themeData = useSelector((state) => state.theme?.data);
  const themeCurrentName = useSelector((state) => state.theme?.currentTheme);
  const userData = useMemo(() => readStoredUserData(), []);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [isPresenter, setIsPresenter] = useState(() => isPresenterValue(userData?.presenter));
  const [questionsInitialized, setQuestionsInitialized] = useState(false);
  const [gameStatus, setGameStatus] = useState("");
  const [showRolePopup, setShowRolePopup] = useState(false);
  const [maxLobbyPlayers, setMaxLobbyPlayers] = useState(() => {
    try {
      const stored = sessionStorage.getItem(TOTAL_USER_COUNT_KEY);
      if (stored != null) return normalizeTotalUserCount(stored);
    } catch {
      // ignore storage failures
    }
    return normalizeTotalUserCount(userData?.totalUserCount);
  });

  const userId = String(userData?.userId ?? userData?.userid ?? userData?.id ?? "").trim();
  const sessionId = String(userData?.sessionId ?? "").trim();
  const lobbyName = useMemo(
    () => resolveLobbyName(location.state, userData),
    [location.state, userData]
  );
  const displayName = getDisplayName(userData);

  useEffect(() => {
    if (!lobbyName) return;
    try {
      sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyName);
    } catch {
      // ignore storage failures
    }
    if (String(userData?.lobby ?? "").trim() === lobbyName) return;
    try {
      const merged = { ...(userData || {}), lobby: lobbyName };
      const payload = JSON.stringify(merged);
      localStorage.setItem("userData", payload);
      sessionStorage.setItem("userData", payload);
    } catch {
      // ignore storage failures
    }
  }, [lobbyName, userData]);

  useEffect(() => {
    if (questionsInitialized) return;
    if (!userId || !sessionId || !lobbyName) return;

    const organizationId = String(userData?.organizationId ?? "").trim();
    const themeName = String(themeData?.themename ?? themeCurrentName ?? "").trim();
    if (!adminToken || !organizationId || !themeName) return;

    let cancelled = false;

    const initializeQuestions = async () => {
      try {
        await initQuestions({
          userId,
          sessionId,
          organizationId,
          email: userData?.email || "",
          fullName: displayName,
          themeName,
          token: adminToken,
          lobby: lobbyName,
          link: "",
          loginPin: "",
        });
        if (!cancelled) setQuestionsInitialized(true);
      } catch (e) {
        console.error("Failed to initialize questions on waiting page:", e);
      }
    };

    initializeQuestions();

    return () => {
      cancelled = true;
    };
  }, [
    adminToken,
    displayName,
    lobbyName,
    questionsInitialized,
    sessionId,
    themeCurrentName,
    themeData?.themename,
    userData?.email,
    userData?.organizationId,
    userId,
  ]);

  const handlePlayNow = async () => {
    if (starting) return;
    setError("");

    if (!isPresenter) {
      setError("Only presenter can start the game.");
      return;
    }

    if (players.length !== maxLobbyPlayers) {
      setError("Waiting for all players to join...");
      return;
    }

    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      return;
    }

    try {
      setStarting(true);

      // 2) Create shared lobby/game state doc (idempotent).
      const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
      const puzzleStateRef = doc(
        database,
        `lobbies/${sessionId}/lobby/${lobbyName}/state/puzzle`
      );

      await runTransaction(database, async (tx) => {
        const lobbySnap = await tx.get(gameDocRef);
        const puzzleSnap = await tx.get(puzzleStateRef);
        const lobbyData = lobbySnap.exists() ? (lobbySnap.data() || {}) : {};
        const existingStatus = String(lobbyData?.status ?? "").trim().toLowerCase();
        if (existingStatus === "play") return;

        // Assign roles once.
        if (lobbyData?.rolesAssigned !== true) {
          const liveUsers = players.slice(0, maxLobbyPlayers);
          const ids = liveUsers
            .map((p) => String(p?.userId || "").trim())
            .filter(Boolean);
          if (ids.length !== maxLobbyPlayers) {
            throw new Error("Waiting for all players to join...");
          }

          const shuffled = shuffleArrayDeterministic(ids, `${sessionId}-${lobbyName}-roles`);
          const imposterId = shuffled[0];
          const jesterId = maxLobbyPlayers === 4 ? shuffled[1] : "";

          // Stable per-game color assignment. Every client reaches the same
          // shuffled palette because the seed is identical, so all players see
          // the same color for the same userId.
          const shuffledColors = shuffleArrayDeterministic(
            PLAYER_COLOR_PALETTE,
            `${sessionId}-${lobbyName}-colors`
          );

          for (let i = 0; i < ids.length; i++) {
            const pid = ids[i];
            const role =
              pid === imposterId ? "imposter" : pid === jesterId ? "jester" : "player";
            const color = shuffledColors[i % shuffledColors.length];
            const uref = doc(
              database,
              `lobbies/${sessionId}/lobby/${lobbyName}/users/${pid}`
            );
            tx.set(
              uref,
              { role, color, activity: "idle", heldTileId: null, holdUntilMs: null },
              { merge: true }
            );
          }
          tx.set(gameDocRef, { rolesAssigned: true }, { merge: true });
        }

        // Initialize / reset puzzle state for the new round.
        const gridSize = parseGridSize(lobbyData?.grid_size, 6);
        const totalTimeSec = parseMmSsToSeconds(lobbyData?.total_time, 600);
        const votingStartTimeSec = Math.max(
          0,
          Math.min(totalTimeSec, parseMmSsToSeconds(lobbyData?.voting_start_time, 480))
        );
        const votingDurationSec = Math.max(
          1,
          parseMmSsToSeconds(lobbyData?.voting_duration, 60)
        );
        const freshVoting = {
          isOpen: false,
          startedAtMs: 0,
          endsAtMs: 0,
          closedAtMs: 0,
          cooldownUntilMs: 0,
          durationSec: votingDurationSec,
          votesByUserId: {},
          result: null,
          finalOutcome: null,
          finalReportSynced: false,
          finalReportSyncedBy: "",
          finalReportSyncedAt: 0,
          reportSyncInProgress: false,
        };

        if (!puzzleSnap.exists()) {
          const n = gridSize * gridSize;
          const tiles = shuffleArrayDeterministic(
            Array.from({ length: n }, (_, i) => i),
            `${sessionId}-${lobbyName}-tiles`
          );

          tx.set(
            puzzleStateRef,
            {
              gridSize,
              imageKey: String(lobbyData?.puzzle_image || "").trim(),
              tiles,
              startedAtMs: Date.now(),
              totalTimeSec,
              votingStartTimeSec,
              votingDurationSec,
              updatedAtMs: Date.now(),
              version: 0,
              lastMove: { moveId: "", by: "", atMs: Date.now() },
              voting: freshVoting,
            },
            { merge: true }
          );
          const puzzleTilesRef = collection(
            database,
            `lobbies/${sessionId}/lobby/${lobbyName}/puzzleTiles`
          );
          for (let pos = 0; pos < tiles.length; pos++) {
            tx.set(
              doc(puzzleTilesRef, String(pos)),
              {
                pos,
                tileId: Number(tiles[pos]),
                updatedAtMs: Date.now(),
              },
              { merge: true }
            );
          }
        } else {
          // Reset round-scoped fields so a re-used lobby starts clean.
          tx.set(
            puzzleStateRef,
            {
              startedAtMs: Date.now(),
              totalTimeSec,
              votingStartTimeSec,
              votingDurationSec,
              updatedAtMs: Date.now(),
              voting: freshVoting,
            },
            { merge: true }
          );
        }

        // Move lobby to play and clear any prior gameover summary.
        tx.set(
          gameDocRef,
          {
            status: "play",
            time: "30",
            turn: "",
            totalUserCount: maxLobbyPlayers,
            questions: themeData?.questions || [],
            currentQuestion: 0,
            startedAtMs: Date.now(),
            winnerSummary: null,
            gameoverAt: 0,
          },
          { merge: true }
        );
      });
    } catch (e) {
      console.error("Play Now failed:", e);
      setError(String(e?.message || e || "Failed to start game."));
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      setLoading(false);
      return;
    }

    let unsubscribeUsers = null;
    let unsubscribeGame = null;
    let isActive = true;
    const usersPath = `lobbies/${sessionId}/lobby/${lobbyName}/users`;
    const usersRef = collection(database, usersPath);
    const userDocRef = doc(database, `${usersPath}/${userId}`);
    const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);

    const joinCurrentUser = async () => {
      try {
        if (!isActive) return;
        const existingUserDoc = await getDoc(userDocRef);
        if (!existingUserDoc.exists()) {
          const presenter = isPresenterValue(userData?.presenter);
          await setDoc(
            userDocRef,
            {
              userId,
              sessionId,
              lobby: lobbyName,
              organizationId: userData?.organizationId || "",
              name: displayName,
              email: userData?.email || "",
              emp_id: userData?.emp_id || userData?.employeeId || "",
              employeeId: userData?.employeeId || userData?.emp_id || "",
              timestamp: Date.now(),
              status: "live",
              presenter,
            },
            { merge: true }
          );
          if (isActive) setIsPresenter(presenter);
        } else {
          // Keep existing user fields, but mark this user as active on the waiting page.
          const existingPresenter = isPresenterValue(existingUserDoc.data()?.presenter);
          await setDoc(userDocRef, { status: "live" }, { merge: true });
          if (isActive) setIsPresenter(existingPresenter);
        }
      } catch (e) {
        if (isActive) {
          console.error("Failed to upsert current lobby user:", e);
        }
      }
    };

    joinCurrentUser();

    unsubscribeUsers = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        if (!isActive) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const sortedLive = sortLiveUsersForCharacterAssign(
          list.filter((p) => p?.status === "live")
        );
        // Show only users currently on the waiting page.
        setPlayers(sortedLive.slice(0, maxLobbyPlayers));

        const myIdx = sortedLive.findIndex(
          (p) => String(p?.userId || "").trim() === userId
        );
        if (myIdx !== -1) {
          const desired = characterForLobbySlotIndex(myIdx);
          const me = sortedLive[myIdx];
          if (normalizeCharacterName(me.character) !== desired) {
            setDoc(userDocRef, { character: desired }, { merge: true }).catch((err) => {
              console.error("Failed to sync waiting lobby character:", err);
            });
          }
        }

        setLoading(false);
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting lobby snapshot failed:", e);
        setError("Could not load lobby players.");
        setLoading(false);
      }
    );

    // Track shared lobby status so the role-reveal popup can be shown
    // when the game flips to "play" (we no longer auto-navigate here).
    unsubscribeGame = onSnapshot(
      gameDocRef,
      (snap) => {
        if (!isActive || !snap.exists()) return;
        const data = snap.data() || {};
        const status = String(data?.status ?? "").trim().toLowerCase();
        const resolvedTotalUserCount = normalizeTotalUserCount(data?.totalUserCount);
        setMaxLobbyPlayers(resolvedTotalUserCount);
        try {
          sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(resolvedTotalUserCount));
        } catch {
          // ignore storage write failures
        }
        setGameStatus(status);
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting game status snapshot failed:", e);
      }
    );

    return () => {
      isActive = false;
      if (unsubscribeUsers) unsubscribeUsers();
      if (unsubscribeGame) unsubscribeGame();
    };
  }, [displayName, lobbyName, navigate, sessionId, userData?.email, userData?.organizationId, userId, maxLobbyPlayers]);

  const me = useMemo(
    () => players.find((p) => String(p?.userId || "").trim() === userId) || null,
    [players, userId]
  );
  const myRole = useMemo(
    () => String(me?.role || "").trim().toLowerCase(),
    [me]
  );
  const myCharacter = useMemo(
    () =>
      normalizeCharacterName(me?.character) ||
      normalizeCharacterName(userData?.character),
    [me, userData?.character]
  );

  useEffect(() => {
    if (gameStatus === "play" && myRole) {
      setShowRolePopup(true);
    }
  }, [gameStatus, myRole]);

  const handleRolePopupNext = () => {
    setShowRolePopup(false);
    navigate("/puzzle");
  };

  const roleConfig = useMemo(() => {
    switch (myRole) {
      case "imposter":
        return {
          themeClass: "role-popup-imposter",
          title: "IMPOSTER",
          task: "Sabotage the puzzle. Don't let the team solve it before the timer ends.",
        };
      case "jester":
        return {
          themeClass: "role-popup-jester",
          title: "JESTER",
          task: "Get yourself voted out without giving it away.",
        };
      default:
        return {
          themeClass: "role-popup-player",
          title: "PLAYER",
          task: "Solve the puzzle as a team and vote out the imposter before time runs out.",
        };
    }
  }, [myRole]);

  const slots = useMemo(() => {
    const filled = players.map((p, idx) => ({
      joined: true,
      label: getPlayerLabel(p, String(p?.userId || "") === userId ? userData : null, idx + 1),
      order: idx + 1,
      isMe: String(p?.userId || "") === userId,
      character: normalizeCharacterName(p?.character),
    }));
    while (filled.length < maxLobbyPlayers) {
      filled.push({
        joined: false,
        label: "......",
        order: filled.length + 1,
        isMe: false,
        character: "",
      });
    }
    return filled;
  }, [maxLobbyPlayers, players, userData, userId]);

  return (
    <div className="waiting-page">
      <div className="waiting-overlay" />

      {loading ? <p className="waiting-message">Loading lobby...</p> : null}
      {error ? <p className="waiting-error">{error}</p> : null}

      <div
        className={`waiting-grid waiting-grid--${maxLobbyPlayers === 3 ? "three" : "four"}`}
      >
        {slots.map((slot, idx) => (
          <div
            key={`${slot.order}-${idx}`}
            className={`waiting-card ${slot.joined ? "joined" : "empty"} ${slot.isMe ? "me" : ""}`}
          >
            <div className="waiting-card-title">
              {slot.joined && slot.label && !/^Player\s+\d+$/i.test(String(slot.label).trim())
                ? slot.label
                : `Player ${slot.order}`}
            </div>
            <div className="waiting-avatar">
              {slot.joined && slot.character ? (
                <img
                  className="waiting-avatar-image"
                  src={`/img/${slot.character}.png`}
                  alt={slot.character}
                />
              ) : null}
            </div>
            <div className={`waiting-status ${slot.joined ? "joined" : "waiting"}`}>
              <span>{slot.joined ? "Joined" : "Waiting"}</span>
              <span
                className={
                  slot.joined ? "waiting-status-icon waiting-status-check" : "waiting-status-icon waiting-status-spinner"
                }
                aria-hidden="true"
              >
                {slot.joined ? "✓" : ""}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="waiting-cta-wrap">
        {isPresenter ? (
          <button
            className="waiting-play-btn"
            type="button"
            disabled={players.length !== maxLobbyPlayers || starting}
            onClick={handlePlayNow}
          >
            {starting ? "Starting..." : "Start Game"}
          </button>
        ) : null}
        <div className="waiting-help-text">
          {players.length === maxLobbyPlayers
            ? "waiting for host to start the game"
            : `waiting for ${maxLobbyPlayers} players to join`}
        </div>
      </div>

      {showRolePopup ? (
        <div className="role-popup-overlay" role="presentation">
          <div
            className={`role-popup-card ${roleConfig.themeClass}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-popup-title"
          >
            <div className="role-popup-avatar">
              {myCharacter ? (
                <img src={`/img/${myCharacter}.png`} alt={myCharacter} />
              ) : (
                <span className="role-popup-avatar-fallback" aria-hidden="true">?</span>
              )}
            </div>
            <h2 id="role-popup-title" className="role-popup-title">
              {roleConfig.title}
            </h2>
            <p className="role-popup-task">{roleConfig.task}</p>
            <button
              type="button"
              className="role-popup-next-btn"
              onClick={handleRolePopupNext}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
